import { Input } from '~/components/primitives';
import { Switch } from '~/components/ui/switch';
import { usePortalCtx } from '~/features/global/portal';
import { useVATInclPreference } from '~/features/portals/money';
import { ActivityFilterBar_Mobile_NumberFilter, type ActivityFilterConfig } from './shared';

export const BUDGET_RANGE_FILTER_CONFIG: ActivityFilterConfig<{
    minBudget?: number | null;
    maxBudget?: number | null;
}> = {
    key: 'budget',
    label: i18n => `${i18n._('Budget')} ${i18n._('p.p.')}`,
    placeholder: i18n => i18n._('No preference'),
    footNote: i18n => i18n._('This amount does not include catering and any potential extras.'),
    selectionDisplay: (i18n, value) => {
        if (value.minBudget && value.maxBudget)
            return `${i18n._('From')} €${value.minBudget?.toLocaleString() ?? 0} - ${i18n._(
                'To',
            )} €${value.maxBudget?.toLocaleString() ?? 0}`;

        if (value.minBudget) return `${i18n._('From')} €${value.minBudget?.toLocaleString() ?? 0}`;
        if (value.maxBudget) return `${i18n._('To')} €${value.maxBudget?.toLocaleString() ?? 0}`;
    },

    PopupContent(props) {
        const portal = usePortalCtx();
        const vatPref = useVATInclPreference();
        const isBizz = portal.portal === 'BIZZ';

        const handleBudgetChange = (field: 'minBudget' | 'maxBudget', val: string) => {
            const value = parseInt(val.replace(/[^0-9]/g, ''), 10);
            props.onChange(prev => ({
                ...prev,
                [field]: Number.isNaN(value) ? null : value,
                ...(field === 'minBudget' && prev.maxBudget && value > (prev.maxBudget ?? 0)
                    ? { maxBudget: value }
                    : {}),
            }));
        };

        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <Input
                        type="number"
                        className="mb-0!"
                        value={props.value.minBudget ?? undefined}
                        placeholder={props.i18n._('From').toLowerCase()}
                        prefix={<span className="text-secondary">€</span>}
                        onChange={e => handleBudgetChange('minBudget', e.currentTarget.value)}
                        step={1}
                    />
                    <div className="px-2">{' - '}</div>
                    <Input
                        type="number"
                        className="mb-0!"
                        value={props.value.maxBudget ?? undefined}
                        placeholder={props.i18n._('To').toLowerCase()}
                        prefix={<span className="text-secondary">€</span>}
                        onChange={e => handleBudgetChange('maxBudget', e.currentTarget.value)}
                        step={1}
                        autoFocus
                    />
                </div>
                {!isBizz && (
                    <div className="flex items-center justify-between">
                        <label
                            htmlFor="popover-vat-toggle"
                            className="cursor-pointer text-sm text-blue"
                        >
                            {props.i18n._('Show prices VAT incl.')}
                        </label>
                        <Switch
                            id="popover-vat-toggle"
                            size="sm"
                            checked={vatPref.enabled}
                            onCheckedChange={vatPref.setEnabled}
                        />
                    </div>
                )}
            </div>
        );
    },

    mobile: {
        icon: 'wallet',
        ctaText: i18n => i18n._('Choose budget'),
        Component(props) {
            return (
                <ActivityFilterBar_Mobile_NumberFilter
                    value={props.value.maxBudget ?? null}
                    onChange={value =>
                        props.onChange(prev => ({
                            ...prev,
                            minBudget: null,
                            maxBudget: value,
                        }))
                    }
                    prefix="€"
                    suffix="/p.p." // TODO: internationalize
                    placeholder={props.i18n._('Choose budget')}
                />
            );
        },
    },
};
